"use client";
import { Pagination } from "@/components/bnp-ui";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";

import { PropertyService, ExperimentalPropertyService } from "@/services";
import type { ExperimentalFeedSortKey } from "@/services";
import { HomepagePropertiesQuery } from "@/types";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import { useQuery } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleAd from "@/components/shared/GoogleAd";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import { track } from "@/utils/track";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import { getAddress } from "@/utils/location";
import parseHttpError from "@/utils/parseHttpError";
import ModernMinimalistFilters from "@/components/bnp-ui/property-filter/ModernPropertyFilters";
import PropertyListingSkeleton from "@/components/bnp-ui/listing-card/PropertyListingSkeleton";
import UrlOptimizedListingCardContainer from "@/components/seo/UrlOptimizedListingCardContainer";
import NoPropertyFound from "@/components/bnp-ui/helper_ui/NoPropertyFound";
import RequestErrorState from "@/components/bnp-ui/requestError";
import { Property } from "@/types/property";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import { ContentSidebar } from "@/components/layouts/Others/ContentSidebar";
import LocationRequest from "@/components/functional/LocationRequest";
import NearbyCities from "@/components/shared/NearbyCities";
import Link from "next/link";
import {
  buildListingPath,
  buildLocationPath,
} from "@/lib/seo/property-urls";
import { useLocation } from "@/contexts/LocationContext";
const runtimeEnvironment = getRuntimeEnvironment();

// Helper utility to safely extract a single string from a Next.js query parameter
const cleanQueryString = (
  val: string | string[] | undefined,
): string | undefined => {
  if (!val) return undefined;
  return Array.isArray(val) ? val[0] : val;
};

// Also for booleans coming from query string strings ("true" / "false")
const cleanQueryBool = (val: any): boolean | undefined => {
  if (val === undefined || val === null || val === "") return undefined;
  return val === "true" || val === true;
};

// Non-production builds exercise the experimental sort endpoints; production
// stays on the legacy feed endpoints.
const { isProduction } = getRuntimeEnvironment();
const SITE_ORIGIN =
  process.env.NEXT_PUBLIC_BASE_URL || "https://www.findnigeriaproperties.com";

const QUICK_LINKS = [
  { label: "For Sale", href: buildListingPath({ listingType: "sale" }) },
  { label: "For Rent", href: buildListingPath({ listingType: "rent" }) },
  { label: "For Lease", href: buildListingPath({ listingType: "lease" }) },
  { label: "Shortlets", href: buildListingPath({ listingType: "shortlet" }) },
  {
    label: "Land",
    href: buildListingPath({ listingType: "sale", category: "land" }),
  },
];

const PILL_CLASS =
  "inline-flex max-w-full items-center rounded-full border border-teal-200 bg-teal-50 px-4 py-2 font-medium leading-5 text-teal-900 shadow-sm transition-colors hover:border-teal-400 hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2";

interface FeaturedState {
  name: string;
  slug: string;
  listing_count: number;
}

interface PropertiesProps {
  initialData?: {
    data: Property[];
    metadata: { totalCount: number };
  };
  featuredStates?: FeaturedState[];
}

const Properties = ({ initialData, featuredStates = [] }: PropertiesProps) => {
  //const pageAds = usePageAds(AdPage.PROPERTIES);
  const pageAds = usePageAds(AdPage.HOME);
  const footerAds = pageAds?.footerAds || [];
  const headerAds = pageAds?.headerAds || [];
  const infeedAds = pageAds?.inFeedAds || [];
  const sidebarAds = pageAds?.sidebarAds || [];
  const popupAds = pageAds?.popupAds || [];

  const router = useRouter();
  const { trackPropertySearched } = usePostHogAnalytics();
  const {
    location,
    state,
    city,
    searchQuery,
    bedroomCount,
    bathroomCount,
    listingType,
    propertyType,
    propertyCategory,
    realtorId,
    maxPrice,
    minPrice,
    furnished,
    serviced,
    shared,
    addedToSite,
    sort,
    page,
  } = router.query;

  const [screenWidth, setScreenWidth] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">(
    "default",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [activeQuery, setActiveQuery] = useState<
    HomepagePropertiesQuery & { sort?: ExperimentalFeedSortKey }
  >(() => ({
    page: 1,
    pageSize: 24,
    location: cleanQueryString(location),
    state: cleanQueryString(state),
    city: cleanQueryString(city),
    searchQuery: cleanQueryString(searchQuery),
    realtorId: cleanQueryString(realtorId),
    bedroomCount: cleanQueryString(bedroomCount),
    bathroomCount: cleanQueryString(bathroomCount),
    listingType: cleanQueryString(listingType),
    propertyType: cleanQueryString(propertyType),
    propertyCategory: cleanQueryString(propertyCategory),
    maxPrice: cleanQueryString(maxPrice),
    minPrice: cleanQueryString(minPrice),
    furnished: cleanQueryBool(furnished),
    shared: cleanQueryBool(shared),
    serviced: cleanQueryBool(serviced),
    addedToSite: cleanQueryBool(addedToSite),
    sort: cleanQueryString(sort) as ExperimentalFeedSortKey | undefined,
  }));
  const { userLocation } = useLocation();
  const pageSize = 24;

  const trackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTrackRef = useRef<{ page: number; filterCount: number } | null>(
    null,
  );
  const searchedRef = useRef<string | null>(null);

  // Extract filters directly from URL query
  const queryParamsFromUrl = useMemo<
    HomepagePropertiesQuery & { sort?: ExperimentalFeedSortKey }
  >(() => {
    if (!router.isReady) return { page: 1, pageSize };

    const {
      location,
      state,
      city,
      searchQuery,
      bedroomCount,
      bathroomCount,
      listingType,
      propertyType,
      propertyCategory,
      realtorId,
      maxPrice,
      minPrice,
      furnished,
      serviced,
      shared,
      addedToSite,
      sort,
      page,
    } = router.query;

    return {
      page: page ? Number(page) : 1,
      pageSize,
      location: cleanQueryString(location),
      state: cleanQueryString(state),
      city: cleanQueryString(city),
      searchQuery: cleanQueryString(searchQuery),
      realtorId: cleanQueryString(realtorId),
      bedroomCount: cleanQueryString(bedroomCount),
      bathroomCount: cleanQueryString(bathroomCount),
      listingType: cleanQueryString(listingType),
      propertyType: cleanQueryString(propertyType),
      propertyCategory: cleanQueryString(propertyCategory),
      maxPrice: cleanQueryString(maxPrice),
      minPrice: cleanQueryString(minPrice),
      furnished: cleanQueryBool(furnished),
      shared: cleanQueryBool(shared),
      serviced: cleanQueryBool(serviced),
      addedToSite: cleanQueryBool(addedToSite),
      sort: cleanQueryString(sort) as ExperimentalFeedSortKey | undefined,
    };
  }, [router.isReady, router.query]);

  // Sync activeQuery when URL query finishes parsing on load
  useEffect(() => {
    if (router.isReady) {
      setActiveQuery(queryParamsFromUrl);
      if (router.query.page) {
        setCurrentPage(Number(router.query.page));
      }
    }
  }, [router.isReady, queryParamsFromUrl]);

  // True when no filters are applied, matching the unfiltered feed the
  // server prefetched in page.tsx.
  const isDefaultQuery = (
    Object.entries(activeQuery) as [string, unknown][]
  ).every(([key, value]) => {
    if (key === "page" || key === "pageSize") return true;
    return value === undefined;
  });

  const getPropertiesRequest = async (): Promise<any> => {
    try {
      const queryParams: HomepagePropertiesQuery & {
        sort?: ExperimentalFeedSortKey;
      } = {
        ...activeQuery,
        page: currentPage,
        pageSize,
      };

      if (!queryParams.location && userLocation) {
        const address = await getAddress(userLocation.lat, userLocation.lng);

        if (isProduction) {
          const response = await PropertyService.GetPropertiesWithGeo({
            ...queryParams,
            userCurrentLocation: address,
            latitude: userLocation.lat.toString(),
            longitude: userLocation.lng.toString(),
          });
          return response.data;
        }

        const response =
          await ExperimentalPropertyService.GetHomepageProperties({
            ...queryParams,
            userCurrentLocation: address,
            latitude: userLocation.lat.toString(),
            longitude: userLocation.lng.toString(),
          });
        return response.data;
      }

      if (queryParams.realtorId) {
        const response = await PropertyService.GetPropertiesByRealtor({
          realtorId: queryParams.realtorId,
        });
        return response?.data;
      } else {
        if (isProduction) {
          const response =
            await PropertyService.GetPropertiesWithGeo(queryParams);
          return response?.data;
        }

        const response =
          await ExperimentalPropertyService.GetHomepageProperties(queryParams);
        return response?.data;
      }
    } catch (error: any) {
      console.error(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: propertiesData,
    isPending: propertiesIsPending,
    isError: propertiesIsError,
    refetch: propertiesRefetch,
  } = useQuery({
    queryKey: [
      "property",
      activeQuery,
      currentPage,
      pageSize,
      userLocation?.lat,
      userLocation?.lng,
    ],
    queryFn: getPropertiesRequest,
    // CRITICAL FIX: Only run query when router query parameters are ready
    enabled: router.isReady,
    staleTime: 60 * 60 * 1000,
    // Seed the first (unfiltered) page with server-fetched data so SSR/first
    // paint has real content instead of waiting on this client-side fetch.
    initialData:
      currentPage === 1 && isDefaultQuery && !userLocation
        ? initialData
        : undefined,
    initialDataUpdatedAt: 0,
  });

  const handleApplyFilters = (params: Record<string, string>) => {
    const updatedQuery = {
      page: 1,
      pageSize,
      searchQuery: params.searchQuery,
      location: params.location,
      state: params.state,
      city: params.city,
      realtorId: params.realtorId,
      bedroomCount: params.bedroomCount,
      bathroomCount: params.bathroomCount,
      listingType: params.listingType,
      propertyType: params.propertyType,
      propertyCategory: params.propertyCategory,
      maxPrice: params.maxPrice,
      minPrice: params.minPrice,
      furnished: cleanQueryBool(params.furnished),
      shared: cleanQueryBool(params.shared),
      serviced: cleanQueryBool(params.serviced),
      addedToSite: cleanQueryBool(params.addedToSite),
      sort: params.sort as ExperimentalFeedSortKey | undefined,
    };

    setActiveQuery(updatedQuery);
    setCurrentPage(1);

    // Keep the browser URL synchronized with the applied filters
    router.push({
      pathname: router.pathname,
      query: params,
    });
  };

  const handlePageChange = (pageNumber: number) => {
    const newQuery = {
      ...router.query,
      page: pageNumber.toString(),
    };

    router.push({
      pathname: router.pathname,
      query: newQuery,
    });
  };

  const sortProperties = (data: Property[]) => {
    if (sortOrder === "default") return data;

    return [...data].sort((a, b) => {
      const aValue = a.total_package ?? 0;
      const bValue = b.total_package ?? 0;

      const aNum =
        typeof aValue === "number" && Number.isFinite(aValue) ? aValue : 0;
      const bNum =
        typeof bValue === "number" && Number.isFinite(bValue) ? bValue : 0;

      return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
    });
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOrder(event.target.value as "default" | "asc" | "desc");
  };

  const displayedProperties = propertiesData?.data
    ? sortProperties(propertiesData.data)
    : [];

  useEffect(() => {
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);

    trackTimerRef.current = setTimeout(() => {
      const filterCount = [
        location,
        state,
        city,
        bedroomCount,
        bathroomCount,
        listingType,
        propertyType,
        realtorId,
        maxPrice,
        minPrice,
        furnished,
        serviced,
        shared,
        addedToSite,
      ].filter(Boolean).length;

      const snapshot = { page: currentPage, filterCount };
      const prev = prevTrackRef.current;

      // Skip if nothing meaningful changed (same page + same filter count).
      if (
        prev &&
        prev.page === snapshot.page &&
        prev.filterCount === snapshot.filterCount
      ) {
        return;
      }

      prevTrackRef.current = snapshot;

      track("user_explored_properties", {
        page: currentPage,
        ...(filterCount > 0 && { filter_count: filterCount }),
      });
    }, 300);

    return () => {
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    };
  }, [
    currentPage,
    location,
    state,
    city,
    bedroomCount,
    bathroomCount,
    listingType,
    propertyType,
    realtorId,
    maxPrice,
    minPrice,
    furnished,
    serviced,
    shared,
    addedToSite,
  ]);

  useEffect(() => {
    if (!propertiesData || !router.isReady) return;

    const signature = JSON.stringify({ activeQuery, currentPage, sortOrder });
    if (searchedRef.current === signature) return;
    searchedRef.current = signature;

    const excludedKeys = ["page", "pageSize", "searchQuery"];
    const filtersUsed = Object.entries(activeQuery)
      .filter(
        ([key, value]) =>
          !excludedKeys.includes(key) &&
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== false,
      )
      .map(([key]) => key);

    trackPropertySearched({
      search_term: activeQuery.searchQuery,
      location:
        activeQuery.location || activeQuery.city || activeQuery.state || undefined,
      property_type: activeQuery.propertyType,
      listing_type: activeQuery.listingType,
      bedrooms: activeQuery.bedroomCount
        ? Number(activeQuery.bedroomCount)
        : undefined,
      min_budget: activeQuery.minPrice ? Number(activeQuery.minPrice) : undefined,
      max_budget: activeQuery.maxPrice ? Number(activeQuery.maxPrice) : undefined,
      results_count:
        propertiesData?.metadata?.totalCount ??
        propertiesData?.data?.length ??
        0,
      sort: sortOrder,
      filters_used: filtersUsed,
    });
  }, [
    propertiesData,
    activeQuery,
    currentPage,
    sortOrder,
    router.isReady,
    trackPropertySearched,
  ]);

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
    } else {
      setCurrentPage(1);
    }
  }, [page]);

  useEffect(() => {
    const updateWidth = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const propertyListingStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: displayedProperties
      .slice(0, 10)
      .map((property, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "RealEstateListing",
          name: property.description || "Property Listing",
          url: `${SITE_ORIGIN}/property/${property.id}`,
          image:
            property.image_url?.[0] ||
            `${SITE_ORIGIN}/images/default-property.jpg`,
          ...(Number(property.price || property.total_package) > 0
            ? {
                offers: {
                  "@type": "Offer",
                  price: Number(property.price || property.total_package),
                  priceCurrency: property.currency || "NGN",
                  ...(property.availability_status === "available"
                    ? { availability: "https://schema.org/InStock" }
                    : {}),
                },
              }
            : {}),
          address: {
            "@type": "PostalAddress",
            ...(property.city ? { addressLocality: property.city } : {}),
            ...(property.state ? { addressRegion: property.state } : {}),
            addressCountry: "NG",
          },
        },
      })),
  };

  return (
    <>
      {displayedProperties.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(propertyListingStructuredData).replace(
              /</g,
              "\\u003c",
            ),
          }}
        />
      )}
      <HomeWrapper>
        {headerAds[0] && (
          <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
            <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
          </div>
        )}

        <>
          <div className="w-full container mx-auto gap-4 mb-5 sticky top-[64px] lg:top-[94px] z-20 bg-white">
            <ModernMinimalistFilters onApply={handleApplyFilters} />
          </div>
        </>
        {
         !runtimeEnvironment.isProduction &&  (<>
        <NearbyCities />
        <nav
          aria-label="Browse property listings by type and location"
          className="mx-auto mb-6 max-w-6xl px-4"
        >
          <ul className="flex flex-wrap gap-2.5 text-sm">
            {QUICK_LINKS.map((link) => (
              <li key={link.href}>
                <Link className={PILL_CLASS} href={link.href}>
                  <span className="whitespace-normal [overflow-wrap:anywhere]">
                    {link.label}
                  </span>
                </Link>
              </li>
            ))}
            {featuredStates.map((state) => (
              <li key={state.slug}>
                <Link className={PILL_CLASS} href={buildLocationPath(state.slug)}>
                  <span className="whitespace-normal [overflow-wrap:anywhere]">
                    Properties in{" "}
                    {state.name === "Federal Capital Territory"
                      ? "Abuja (FCT)"
                      : state.name}
                  </span>
                </Link>
              </li>
            ))}
            <li>
              <Link className={PILL_CLASS} href="/properties/in">
                <span className="whitespace-normal [overflow-wrap:anywhere]">
                  Browse by location
                </span>
              </Link>
            </li>
          </ul>
        </nav>
         </>)
        }

        <ContentSidebar
          sidebarContent={
            sidebarAds?.[0] && (
              <div className="w-full mx-auto px-4 pt-4 pb-2">
                <AdRenderer
                  ad={sidebarAds[0]}
                  placement={AdPlacement.SIDEBAR}
                />
              </div>
            )
          }
          topOffset={200}
        >
          <div className={containerWidth}>
            <div className="flex flex-col items-center w-full  mb-8 lg:mb-16 overflow-x-clip">
              <LocationRequest />

              {propertiesIsPending ? (
                <PropertyListingSkeleton />
              ) : propertiesIsError ? (
                <RequestErrorState
                  title="Failed to load properties"
                  errorMessage="We couldn't retrieve properties at this time. Please try again."
                  onRetry={async () => {
                    await propertiesRefetch();
                  }}
                />
              ) : (
                <>
                  {displayedProperties.length > 0 ? (
                    <>
                      <div className="flex flex-col lg:w-fit w-[100%]">
                        <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full">
                          {displayedProperties
                            .slice(0, 6)
                            .map((property: Property) => (
                              <UrlOptimizedListingCardContainer
                                key={property.id}
                                property={property}
                              />
                            ))}
                        </div>
                      </div>
                      {/* In-feed ad placed between the first two rows and the
                          second two rows (after the 6th property) */}
                      {infeedAds[0] && displayedProperties.length > 6 && (
                        <div className="col-span-1 md:col-span-2 lg:col-span-3 w-full my-4">
                          <AdRenderer
                            ad={infeedAds[0]}
                            placement={AdPlacement.IN_FEED}
                          />
                        </div>
                      )}

                      <div className="flex flex-col lg:w-fit w-[100%]">
                        {" "}
                        <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full">
                          {displayedProperties
                            .slice(6)
                            .map((property: Property) => (
                              <UrlOptimizedListingCardContainer
                                key={property.id}
                                property={property}
                              />
                            ))}
                        </div>
                      </div>
                      <Pagination
                        metadata={propertiesData?.metadata}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        className="w-full mt-8"
                        pageSize={pageSize}
                      />
                    </>
                  ) : (
                    <NoPropertyFound />
                  )}
                </>
              )}
            </div>
          </div>
        </ContentSidebar>
        <div className=" w-[100%] lg:w-[800px] flex justify-center my-8">
          <GoogleAd
            adSlot={process.env.NEXT_PUBLIC_AD_SLOT_BANNER || ""}
            adFormat="horizontal"
            fullWidthResponsive={true}
            className="w-full"
          />
        </div>
        {popupAds[0] && (
          <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
        )}
        {footerAds[0] && (
          <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
            <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
          </div>
        )}
      </HomeWrapper>
    </>
  );
};

export default Properties;

/*

            <div>
              <div className="">
                {false &&
                  displayedProperties.length > 0 &&
                  !propertiesIsLoading && (
                    <div className="hidden lg:block lg:min-w-[300px] lg:max-w-[300px] lg:top-20">
                      <div className="w-full flex lg:ml-[-30px] mt-4 justify-end mb-4">
                        <select
                          value={sortOrder}
                          onChange={handleSortChange}
                          className="px-4 py-2 border rounded-md text-grey-7 bg-white outline-none"
                        >
                          <option value="default">Default Order</option>
                          <option value="asc">Price: Low to High</option>
                          <option value="desc">Price: High to Low</option>
                        </select>
                      </div>

                      <PropertyFilters />
                      {sidebarAds[0] && (
                        <div className="mt-4">
                          <SidebarAd
                            ad={sidebarAds[0]}
                            sticky={true}
                            stickyOffset={400}
                          />
                        </div>
                      )}
                      <div className="sticky top-20 z-10 mt-4">
                        <GoogleAd
                          adSlot={process.env.NEXT_PUBLIC_AD_SLOT_SIDEBAR || ""}
                          adFormat="rectangle"
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>
*/
